import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../AuthProvider';
import { ArrowLeft, Eye, EyeOff, Loader2 } from 'lucide-react';
import { companyApiService } from '../../services/companyApi';
import api from '../../services/api';

const MobileLoginPage: React.FC = () => {
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [companyLogo, setCompanyLogo] = useState<string>('');

  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, login, loading } = useAuth();

  // Redirection si déjà connecté
  useEffect(() => {
    if (loading) return;
    if (isAuthenticated) {
      navigate('/', { replace: true });
    }
  }, [loading, isAuthenticated, navigate]);

  // Récupération message après inscription
  useEffect(() => {
    const state = location.state as any;
    if (state?.message) {
      setSuccess(state.message);
      if (state.email) {
        setFormData(prev => ({ ...prev, email: state.email }));
      }
      window.history.replaceState({}, document.title);
    }
  }, [location]);

  // Charger le logo de l'entreprise
  useEffect(() => {
    const loadCompanyLogo = async () => {
      try {
        const profile = await companyApiService.getCompanyProfilePublic();
        if (profile.logo_url) {
          setCompanyLogo(profile.logo_url);
        }
      } catch (error) {
        console.log('Logo non disponible');
      }
    };
    loadCompanyLogo();
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      const response = await api.post('/api/auth/login/', formData);
      const data = response.data;

      login(data.token, data.refresh_token, data.user);
      setSuccess('Connexion réussie !');

      setTimeout(() => {
        const redirect = localStorage.getItem('redirectAfterLogin') || '/';
        localStorage.removeItem('redirectAfterLogin');
        navigate(redirect, { replace: true });
      }, 1000);

    } catch (err: any) {
      let message = 'Impossible de se connecter. Vérifiez vos identifiants.';
      if (err.response?.data?.error) message = err.response.data.error;
      if (err.response?.data?.message) message = err.response.data.message;
      setError(message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-amber-100">
      {/* Header avec bouton retour */}
      <div className="bg-gradient-to-r from-amber-600 to-amber-700 px-4 py-4 shadow-lg">
        <div className="flex items-center justify-between">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-amber-100 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="text-sm font-medium">Retour</span>
          </button>
          <h1 className="text-lg font-semibold text-white">Connexion</h1>
          <div className="w-16"></div> {/* Spacer pour centrer le titre */}
        </div>
      </div>

      {/* Contenu principal */}
      <div className="px-4 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl shadow-xl p-6 border border-amber-200/50"
        >
          {/* Logo */}
          <div className="text-center mb-8">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2 }}
              className="w-20 h-20 mx-auto mb-4"
            >
              {companyLogo ? (
                <img
                  src={companyLogo}
                  alt="IRIS SOFTECH"
                  className="w-full h-full object-contain rounded-2xl"
                />
              ) : (
                <div className="w-full h-full bg-gradient-to-br from-amber-500 to-amber-600 rounded-2xl flex items-center justify-center">
                  <span className="text-white text-2xl font-bold">IS</span>
                </div>
              )}
            </motion.div>
            
            <h2 className="text-2xl font-bold text-gray-800 mb-2">
              Bienvenue !
            </h2>
            <p className="text-gray-600 text-sm">
              Connectez-vous à votre espace IRIS SOFTECH
            </p>
          </div>

          {/* Messages d'alerte */}
          {success && (
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-xl text-sm mb-6 flex items-center gap-2"
            >
              <div className="w-2 h-2 bg-green-500 rounded-full"></div>
              {success}
            </motion.div>
          )}

          {error && (
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm mb-6 flex items-center gap-2"
            >
              <div className="w-2 h-2 bg-red-500 rounded-full"></div>
              {error}
            </motion.div>
          )}

          {/* Formulaire */}
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Adresse email
              </label>
              <input
                type="email"
                name="email"
                required
                value={formData.email}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-transparent bg-white text-gray-900 placeholder-gray-500"
                placeholder="votre@email.com"
              />
            </div>

            {/* Mot de passe */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Mot de passe
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  required
                  value={formData.password}
                  onChange={handleChange}
                  className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-transparent bg-white text-gray-900 placeholder-gray-500"
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                >
                  {showPassword ? (
                    <EyeOff className="w-5 h-5" />
                  ) : (
                    <Eye className="w-5 h-5" />
                  )}
                </button>
              </div>
            </div>

            {/* Bouton de connexion */}
            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-gradient-to-r from-amber-500 to-amber-600 text-white py-4 px-4 rounded-xl font-semibold hover:from-amber-600 hover:to-amber-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
            >
              {isLoading ? (
                <span className="flex items-center justify-center gap-2">
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Connexion en cours...
                </span>
              ) : (
                'Se connecter'
              )}
            </button>
          </form>

          {/* Liens */}
          <div className="mt-8 text-center space-y-4">
            <p className="text-sm text-gray-600">
              Pas encore de compte ?{' '}
              <button
                onClick={() => navigate('/register')}
                className="text-amber-600 font-semibold hover:text-amber-700 transition-colors"
              >
                Créer un compte
              </button>
            </p>

            <div className="pt-4 border-t border-gray-200">
              <button
                onClick={() => navigate('/')}
                className="text-sm text-gray-500 hover:text-gray-700 transition-colors"
              >
                ← Retour à l'accueil
              </button>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Footer décoratif */}
      <div className="px-4 pb-8">
        <div className="text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-100 rounded-full">
            <div className="w-2 h-2 bg-amber-500 rounded-full animate-pulse"></div>
            <span className="text-xs text-amber-700 font-medium">
              Plateforme sécurisée IRIS SOFTECH
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MobileLoginPage;
